import type { AgenticLogicRuleRunDto } from "@intelligems/ig-types/dtos/agentic-logic/rule-runs";

export type RunField = keyof AgenticLogicRuleRunDto;

export interface RunFieldGroup {
  title: string;
  fields: readonly RunField[];
}

/**
 * Run fields the detail view renders with dedicated UI rather than as a
 * label/value row. `items` is the list of products the run returned: a row of
 * stringified objects would be unreadable, so it gets its own section — and
 * naming it here, rather than silently dropping it, keeps the "every field is
 * shown somewhere" guarantee below intact.
 */
export const RUN_FIELDS_RENDERED_SEPARATELY = [
  "items",
] as const satisfies readonly RunField[];

/**
 * The detail view's field order, grouped the way the telemetry schema groups
 * them. Typed against the DTO so a field added to the run row is a compile
 * error here until it is placed — the readout is meant to show everything the
 * event carries, and this is what keeps that true.
 */
export const RUN_FIELD_GROUPS: readonly RunFieldGroup[] = [
  {
    title: "Identity",
    fields: [
      "invocationId",
      "orgId",
      "shopId",
      "surface",
      "placementId",
      "visitorId",
      "variationId",
      "evaluationSource",
      "sessionId",
      "pageViewId",
      "checkoutTokenHash",
      "browserVisibility",
    ],
  },
  {
    title: "Timing",
    fields: ["evaluatedAtMs", "durationMs", "deadlineMs"],
  },
  {
    title: "Engine",
    fields: [
      "ruleId",
      "ruleVersion",
      "templateId",
      "templateVersion",
      "branchFired",
      "branchFiredInstance",
      "fallbackDepth",
      "outcome",
      "errorClass",
    ],
  },
  {
    title: "Broker",
    fields: [
      "executionTier",
      "fallback",
      "classifierBreakerTripped",
      "itemCount",
      "readCache",
    ],
  },
];

// Every DTO key must be placed in exactly one group, or be named in
// RUN_FIELDS_RENDERED_SEPARATELY. A record keyed by the DTO fails to compile
// when a key is missing.
const PLACED: Record<RunField, true> = {
  invocationId: true,
  orgId: true,
  shopId: true,
  surface: true,
  placementId: true,
  visitorId: true,
  variationId: true,
  evaluationSource: true,
  sessionId: true,
  pageViewId: true,
  checkoutTokenHash: true,
  browserVisibility: true,
  evaluatedAtMs: true,
  durationMs: true,
  deadlineMs: true,
  ruleId: true,
  ruleVersion: true,
  templateId: true,
  templateVersion: true,
  branchFired: true,
  branchFiredInstance: true,
  fallbackDepth: true,
  outcome: true,
  errorClass: true,
  executionTier: true,
  fallback: true,
  classifierBreakerTripped: true,
  itemCount: true,
  items: true,
  readCache: true,
};

void PLACED;

/**
 * The run's read-cache facts on one line: the prefetch's outcome and
 * fresh/stale/miss, then the rule's own reads per kind as the same triple.
 */
export const formatReadCache = (
  readCache: NonNullable<AgenticLogicRuleRunDto["readCache"]>
): string => {
  const triple = (counts: { fresh: number; stale: number; miss: number }) =>
    `${counts.fresh}/${counts.stale}/${counts.miss}`;
  const parts = [
    readCache.prefetch
      ? `prefetch ${readCache.prefetch.status} ${triple(readCache.prefetch)}`
      : undefined,
    readCache.reads === undefined
      ? "reads not recorded"
      : readCache.reads.length === 0
        ? "no cached reads"
        : readCache.reads.map((row) => `${row.kind} ${triple(row)}`).join(", "),
  ];
  return `${parts.filter(Boolean).join(" · ")} (fresh/stale/miss)`;
};

/** Human label for a run field: camelCase split into words, ids kept upper. */
export const runFieldLabel = (field: RunField): string =>
  field
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\bMs\b/, "(ms)")
    .replace(/\bId\b/g, "ID")
    .replace(/^./, (c) => c.toUpperCase());
